import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Field, FieldLabel } from '@/components/ui/field';
import type { WeightGainTrajectory } from './clinicalReferences';
export function WeightTrajectoryEditor({
  value,
  onChange,
}: {
  value: WeightGainTrajectory[];
  onChange: (value: WeightGainTrajectory[]) => void;
}) {
  const update = (i: number, band: WeightGainTrajectory) => onChange(value.map((b, index) => (index === i ? band : b)));
  return (
    <section className="flex flex-col gap-4">
      <h4>Weight gain trajectory by gestational age</h4>
      <p>
        Enter reviewed cumulative gain bounds in kilograms at two or more gestational ages. Bounds are interpolated
        between these points only. No trajectory or medical values are supplied automatically. BMI upper bounds are
        exclusive; fetal count must match.
      </p>
      {value.map((band, i) => (
        <fieldset className="flex flex-col gap-3 rounded border p-3" key={i}>
          <legend>Trajectory {i + 1}</legend>
          <div className="grid gap-3 sm:grid-cols-3">
            {(['fetusCount', 'minBmi', 'maxBmi'] as const).map((key) => (
              <Field key={key}>
                <FieldLabel htmlFor={`trajectory-${i}-${key}`}>
                  {
                    {
                      fetusCount: 'Fetal count',
                      minBmi: 'BMI from (inclusive)',
                      maxBmi: 'BMI below (blank = no upper bound)',
                    }[key]
                  }
                </FieldLabel>
                <Input
                  id={`trajectory-${i}-${key}`}
                  type="number"
                  step={key === 'fetusCount' ? 1 : 'any'}
                  required={key !== 'maxBmi'}
                  value={band[key] === null || !Number.isFinite(band[key]) ? '' : band[key]!}
                  onChange={(event) =>
                    update(i, {
                      ...band,
                      [key]:
                        event.target.value === '' ? (key === 'maxBmi' ? null : Number.NaN) : Number(event.target.value),
                    })
                  }
                />
              </Field>
            ))}
          </div>
          <Field>
            <FieldLabel htmlFor={`trajectory-${i}-citation`}>Reviewed source and practice rationale</FieldLabel>
            <Textarea
              id={`trajectory-${i}-citation`}
              required
              value={band.citation}
              onChange={(event) => update(i, { ...band, citation: event.target.value })}
            />
          </Field>
          {band.points.map((point, j) => (
            <div key={j} className="grid items-end gap-3 sm:grid-cols-4">
              {(['gaDays', 'lowKg', 'highKg'] as const).map((key) => (
                <Field key={key}>
                  <FieldLabel htmlFor={`trajectory-${i}-${j}-${key}`}>
                    {
                      {
                        gaDays: 'Gestational age (days)',
                        lowKg: 'Lower cumulative gain (kg)',
                        highKg: 'Upper cumulative gain (kg)',
                      }[key]
                    }
                  </FieldLabel>
                  <Input
                    id={`trajectory-${i}-${j}-${key}`}
                    type="number"
                    step={key === 'gaDays' ? 1 : 'any'}
                    required
                    value={Number.isFinite(point[key]) ? point[key] : ''}
                    onChange={(event) =>
                      update(i, {
                        ...band,
                        points: band.points.map((p, index) =>
                          index === j
                            ? { ...p, [key]: event.target.value === '' ? Number.NaN : Number(event.target.value) }
                            : p,
                        ),
                      })
                    }
                  />
                </Field>
              ))}
              <Button
                type="button"
                variant="outline"
                disabled={band.points.length <= 2}
                onClick={() => update(i, { ...band, points: band.points.filter((_, index) => index !== j) })}>
                Remove point
              </Button>
            </div>
          ))}
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                update(i, {
                  ...band,
                  points: [...band.points, { gaDays: Number.NaN, lowKg: Number.NaN, highKg: Number.NaN }],
                })
              }>
              Add trajectory point
            </Button>
            <Button type="button" variant="outline" onClick={() => onChange(value.filter((_, index) => index !== i))}>
              Remove trajectory
            </Button>
          </div>
        </fieldset>
      ))}
      <Button
        type="button"
        variant="outline"
        onClick={() =>
          onChange([
            ...value,
            {
              fetusCount: Number.NaN,
              minBmi: Number.NaN,
              maxBmi: null,
              citation: '',
              points: [
                { gaDays: Number.NaN, lowKg: Number.NaN, highKg: Number.NaN },
                { gaDays: Number.NaN, lowKg: Number.NaN, highKg: Number.NaN },
              ],
            },
          ])
        }>
        Add reviewed trajectory
      </Button>
    </section>
  );
}
